// Constants used across the scoring system. Derived from the entity
// enums in docs/score/architecture.md.

// #2786 — the ONE canonical dressage judge display/posting order for the
// whole system (arena left to right, and the printed signature-line order).
// USEF Rulebook Chapter DR, DR123.2 (public scoreboard) and DR123.3
// (Individual Classification / final posted results), both verbatim: "When
// multiple judges officiate from different positions, scores must be posted
// in the following order: E, H, C, M, B." This is federation-mandated, not a
// display preference. Source: docs/rules/usef/rulebook-dr-dressage-2026.pdf,
// (c) USEF 2026 (see docs/rules/README.md for the inventory entry). Before
// this card the value here was ["C", "E", "M", "B", "H"] (the old printed
// signature-line order, no rule backing found for it), and five other files
// had grown private copies of the correct E,H,C,M,B order; this is now the
// single source, consumed via resolveClassPanel and sortJudgesForDisplay
// below (utils/calculation.js).
export const JUDGE_POSITIONS = ["E", "H", "C", "M", "B"];

// Card #1750 (equitation class grid), settled per #1748's review thread: a
// panel-consensus sheet (DSE/DSM, 2-3 co-signing judges) has no single real
// judge identity to key ScoreSheet.judgeId on, but that field is a required,
// non-nullable abstract booth-position slot already (see JUDGE_POSITIONS and
// Class.scribeAssignments), not a real judge id — confirmed by
// offloadPanelSignature's own comment in apps/score-api/src/lib/signatures.js.
// Every ScoreSheet the class grid creates for a panel-consensus test is keyed
// to this fixed slot, matching the young-horse precedent of hard-coding "C"
// as the technical-judge convention position. This closes the #1748 seam
// (isAssignedScribeForSheet's pre-panel-branch check) by construction: an
// admin setting up a panel-consensus class must assign the scribe to
// position C. See docs/score/equitation-class-sheet-design.md sections 5-6.
export const PANEL_CONSENSUS_JUDGE_POSITION = "C";

// Card #1992 — "presiding judge" is defined on the card as judge position C,
// always, for any panel size. Distinct from PANEL_CONSENSUS_JUDGE_POSITION
// above (same value, different concept: that one is the fixed seat a
// panel-consensus sheet is co-signed under; this one is the seat that alone
// may raise the scribe scoresheet's No Show action). Kept as its own named
// export so a reader of either call site sees which rule it's citing.
export const PRESIDING_JUDGE_POSITION = "C";

export const PERSON_TYPES = {
  RIDER: "RIDER",
  JUDGE: "JUDGE",
  SCRIBE: "SCRIBE",
};

// #768 — ENTERING_RING sits between PENDING and IN_PROGRESS: C's "Bell"
// press (#767's arena-entry timer) persists it the instant the horse starts
// its approach, before any ScoreSheet exists to derive IN_PROGRESS off of.
// "In Ring" then persists IN_PROGRESS the same way. Both are stored, not
// derived, so every surface (not just C) can see a ride has started — see
// score-common/utils/ride-status.js's deriveRideStatus for how this is
// surfaced pre-sheet, and ride-queue.js's isCurrentCandidate for why it also
// counts as "the ride in the ring" for ring-queue/next-ride purposes.
export const RIDE_STATUSES = {
  PENDING: "PENDING",
  ENTERING_RING: "ENTERING_RING",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  SCRATCHED: "SCRATCHED",
};

// Card #916 (part A) — a SCRATCHED ride can optionally carry a term reason,
// literally FVD's own native tblRide.chrTermReason strings (confirmed on the
// card). Card #1073 (separate) writes these back to FVD via foxbox; this repo
// only persists them on the Ride so that later write-back has something to
// send. Keep these exact strings — they are NOT freely relabelable.
export const RIDE_TERM_REASONS = {
  SCRATCH: "Scratch",
  NO_SHOW: "No Show",
};

// Card #2053 (part B, workstream scoring-truth, split from #2017/#1998 supercard
// item 8) — the federation's own dressage-ride-termination vocabulary, per
// USEF Rulebook Chapter DR, DR123.3 (Scoring, Classification and Prize-Giving),
// confirmed against the committed PDF (docs/rules/usef/rulebook-dr-dressage-2026.pdf)
// by card #2052 (part A), recorded in docs/score/term-reason-vocabulary.md §1:
// "the words 'scratched', 'excused', 'eliminated', or 'no show' ... must appear
// after the competitor's name in the result sheet." Exactly these four labels —
// NOT Evin's originally proposed "eliminated / withdrew / retired / excused"
// (DR123.3 prints "scratched" as the noun, not "withdrew"; "retired" has no
// DR-chapter equivalent at all, see the doc's §1 table).
//
// This is a DISTINCT field from RIDE_TERM_REASONS above: that one is the
// pre-existing #916 bidirectional FVD wire passthrough (raw FVD
// tblRide.chrTermReason strings on ingest/egress, "NOT freely relabelable" per
// its own comment) and must not be renamed, repurposed, or widened. This new
// vocabulary lives on ride.terminationReason (see schemas/validation.js and
// utils/ride-status.js), never on ride.termReason.
export const TERM_REASONS = {
  SCRATCHED: "Scratched",
  EXCUSED: "Excused",
  ELIMINATED: "Eliminated",
  NO_SHOW: "No Show",
  // Card #2795 -- a fifth stored word: a rider enters, the test goes badly,
  // and they stop. NOT one of DR123.3's four printed words (see above), so
  // a Withdrawn ride still has to print one of those four on the paper
  // sheet; that mapping is a rendering decision recorded on card #2795 and
  // is NOT made here. Storage only.
  WITHDRAWN: "Withdrawn",
};

// Card #1930 — the "no score recorded" ride disposition: the honest third exit
// for a ride that was ridden but has no score of its own (WEC 2026-08-21, a
// scribe keyed the wrong ride, one rider's sheet stayed blank, and the only
// way to close the class was to invent marks). It is a ride-level fact
// (ride.noScoreRecorded, markRideNoScoreRecorded/undoRideNoScoreRecorded in
// score-api) and is DELIBERATELY NOT one of TERM_REASONS above: those four are
// the DR123.3 outcomes ("scratched", "excused", "eliminated", "no show") that
// USEF requires printed after a name, and a ridden test with a missing score
// is none of them. It is also not RIDE_STATUSES.SCRATCHED (she rode). Keeping
// it out of deriveRideTermReason means it can never trip termReasonConflicts
// and refuse a scribe's elimination or sheet write (AC8: a scribe can always
// score).
export const NO_SCORE_RECORDED_LABEL = "No score recorded";
// The reason is free text kept on the ride and in its audit trail; bounded so a
// pasted blob cannot bloat the Ride row.
export const NO_SCORE_REASON_MAX_LENGTH = 500;

// Card #567 — FVD write-back (push) status, persisted on a Ride only by the
// one endpoint that owns this contract (score-api's reportFvdPushStatus,
// handlers/ride.js): foxbox reports POSTED once it's written a released
// ride's score into FVD's .sdf, or FAILED with an error if that write
// attempt itself failed. This enum deliberately has no PENDING/NOT_POSTED
// member of its own — an unreported ride simply carries no fvdPushStatus key
// at all (undefined, never fabricated/defaulted to POSTED). That per-RIDE
// absence is what feeds the per-CLASS NOT_POSTED aggregate below.
export const FVD_PUSH_STATUSES = {
  POSTED: "POSTED",
  FAILED: "FAILED",
};

// #3185 - the Review Queue's fixed list of ISSUE TYPES. score-api's
// lib/review-queue.js stamps exactly one of these on every item (derived from
// the item's reason, never invented per item) so scoreadmin-fe can show it in
// the one table's "Issue type" column and filter on it. OTHER is only the
// fallback for a reason the catalog has never heard of (the same "an unknown
// reason must not break the queue" posture REASON_CATALOG's DEFAULT_ENTRY has).
export const REVIEW_ISSUE_TYPES = {
  MANUAL_RIDE: "manual_ride",
  FVD_WRITEBACK_FAILURE: "fvd_writeback_failure",
  QUARANTINED_PAYLOAD: "quarantined_payload",
  UNSIGNED_SHEET: "unsigned_sheet",
  CONTENDER_PICK: "contender_pick",
  STATION_RECONCILE: "station_reconcile",
  STALE_WRITE_CONFLICT: "stale_write_conflict",
  SCRIBE_REVIEW_REQUEST: "scribe_review_request",
  TOTALS_DISAGREEMENT: "totals_disagreement",
  FVD_ORPHAN: "fvd_orphan",
  // #3516 - a scribe sheet held because its ride is not on the server.
  ORPHAN_SHEET: "orphan_sheet",
  // #3534 - FVD changed a rider's test after scoring started.
  FVD_TEST_CHANGE: "fvd_test_change",
  OTHER: "other",
};

// Card #567 — aggregate FVD push state for a Class, computed at READ time
// (getClass/listClasses) over the class's RELEASED rides only (see
// isRideReleased in utils/ride-status.js) — never persisted on the Class
// itself. Consumed by the #554 scoreadmin-fe icon: gray = NOT_POSTED
// (covers both "no released rides yet" and "released rides that simply
// haven't all reported POSTED"), green = POSTED (every released ride
// reported POSTED), red = FAILED (at least one released ride reported
// FAILED, regardless of the rest). See computeClassPushState.
export const CLASS_PUSH_STATES = {
  NOT_POSTED: "NOT_POSTED",
  POSTED: "POSTED",
  FAILED: "FAILED",
};

// Card #2147 — the announcer day spine's `placed` marker, one bit per class,
// for an entire ring in a single call (score-api's getClassResultsSummary,
// GET /class-results-summary?showId=&ring=). Deliberately coarser than the
// per-judge PARTIAL/CONFIRMED/FINAL vocabulary above (DERIVED_RIDE_STATUSES)
// and independent of it: a class is PLACED once isClassPlaced (utils/
// ride-status.js) says every one of its Rides is resolved (a real placing,
// SCRATCHED, a resolvedIntoRideId placeholder, or any DR123.3 term reason),
// the same "class closed" definition the rider self-service sheet gate
// already uses — NOT_PLACED otherwise, including a class with zero Rides
// (isClassPlaced's own empty-list rule). No PARTIAL tier here on purpose:
// the spine only needs to know whether the class is done, never the
// in-between judge-by-judge state, and the summary response never carries
// placings (score-api results.js's own comment on the route).
export const CLASS_RESULTS_STATUSES = {
  PLACED: "PLACED",
  NOT_PLACED: "NOT_PLACED",
};

// #1268 removed force-final entirely (card #506/#1070's admin override):
// nothing ever read the resulting ride.adminForceFinalTreatment
// (fetchConfirmedOrCompletedSheets drops an unconfirmed sheet regardless of
// what was stored), so the FORCE_FINAL_TREATMENTS enum that used to live
// here is gone along with the affordance. Existing prod rows and audit
// entries created before this card are untouched (see #1268's card
// comments for the prod count); ride.adminConfirmedAt/adminConfirmedBy
// themselves stay (packages/score-common/src/schemas/validation.js,
// utils/ride-status.js) because a ride force-finaled before this card still
// needs to read back as FINAL.

// Utility events (card #139) — welfare flags (BLOOD/LAMENESS) a scribe raises
// mid-ride. Persisted on the Ride (utilityEvents array), never on a sheet, so
// every judge position scoring the same ride sees the same flags. Internal
// coordination only — MUST stay off the competitor score sheet, every
// competitor-facing report, and the public rider portal (score.foxvillage.com).
// MEETING_REQUESTED (card #1991) reuses the identical raise/clear plumbing —
// not a welfare flag, but a scribe flagging that a panel meeting is wanted;
// gated FE-side to panel-judged classes only (see utilityEventTypes.js).
export const UTILITY_EVENT_TYPES = {
  BLOOD: "BLOOD",
  LAMENESS: "LAMENESS",
  MEETING_REQUESTED: "MEETING_REQUESTED",
};

export const SYNC_STATUSES = {
  PENDING: "PENDING",
  SYNCING: "SYNCING",
  SYNCED: "SYNCED",
  ERROR: "ERROR",
};

export const SYNC_ACTIONS = {
  CREATE: "CREATE",
  UPDATE: "UPDATE",
};

// #772 (workstream error-deductions, #771-774) — canonical per-class
// error-of-course/test deduction scheme enum. #771's deduction ENGINE and
// #773's scribe APPLY both import this same constant — keep the values
// stable once #772 ships. Human-facing labels live in scoreadmin-fe's i18n
// bundle (never persist the label text itself). POINTS is the default:
// USEF's traditional Points 2/4/elim scale is what every class used before
// this setting existed, so classes lacking an explicit selection (and all
// newly created classes) fall back to it.
export const ERROR_DEDUCTION_SCHEMES = {
  POINTS: "POINTS",
  PERCENT_SENIOR: "PERCENT_SENIOR",
  PERCENT_YOUTH: "PERCENT_YOUTH",
};

// USDF score scale: 0–10, half-point granularity.
export const SCORE_MIN = 0;
export const SCORE_MAX = 10;
export const SCORE_STEP = 0.5;

// Tenth-point (0.1) score granularity. Used by:
//   - #712 freestyle ARTISTIC marks (Test.movements[].section === "artistic").
//   - #889 (bugs #70/#72) every mark of a FEI Young Horse / USEF Four-Year-Old
//     test, which are judged to the tenth across the whole sheet.
// All other marks keep SCORE_STEP. Callers pick the step via
// scoreStepFor(test, section) (utils/scoring.js) and pass it through
// isValidScore/clampScore/autoFormatScore.
export const TENTH_SCORE_STEP = 0.1;

// Back-compat alias: freestyle artistic marks were the original tenth-step
// caller (#712). Prefer TENTH_SCORE_STEP / scoreStepFor for new code.
export const ARTISTIC_SCORE_STEP = TENTH_SCORE_STEP;

// Remarks character caps. R1-UI-012 enforces these on the FE; R1-PLAN-007
// gates the API on the same constants when the API exists (Phase 2). The
// character-counter surfaces when the remaining-chars count drops to
// REMARKS_COUNTER_THRESHOLD or below.
export const REMARKS_MAX_PER_MOVEMENT = 500;
export const REMARKS_MAX_GENERAL = 2000;
export const REMARKS_COUNTER_THRESHOLD = 50;

// #2013 — Score Admin Detailed-view annotation text cap. Every other
// free-text field on ScoreSheet is bounded (REMARKS_MAX_PER_MOVEMENT,
// REMARKS_MAX_GENERAL above); scoreSheetAnnotationSchema.text originally
// shipped with only a min(1), no max. Mirrored into the zod schema
// (score-common/src/schemas/validation.js) and the route's body schema
// maxLength (score-api POST /scoresheet/:id/annotations).
export const ANNOTATION_TEXT_MAX = 1000;

// Card #1648 (workstream `scoreboard`) — originally the 7 public-scoreboard
// mode types (docs/score/scoreboard-design.md §6.2). CORRECTION (round-3
// judge finding on #1648): the claim this comment used to make, that no
// MODE_TYPES vocabulary existed anywhere before this card, was FALSE — #1639
// (rock-on/q3, already merged) shipped apps/score-fe/src/components/
// scoreboard/modeTypes.js's `MODE_TYPE` map first, with kebab-case values
// ("class-summary-detailed" etc.), and its own header says those are "the
// kebab-case string values mode.modeType actually carries". This card had
// independently invented a SCREAMING_SNAKE second vocabulary on the same wire
// field, which is a value-incompatible collision, not a duplicate: after
// merge, whichever spelling actually flows over modeType makes the OTHER
// side's every mode-type comparison false (isSponsorCapable/composeTypedFields
// on the score-api side, or SponsorStrip/QRCorner's fails-closed gates on the
// FE side). The kebab-case spelling wins because it is already pinned by
// shipped Cypress specs that hardcode the literal (e.g.
// apps/score-fe/cypress/e2e/scoreboard-screenshots.cy.js's
// `modeType: "end-of-class-result"`) — those specs assert against the real
// wire value, so the spelling can't move without breaking shipped coverage.
// score-common is now the single source of truth: score-fe's modeTypes.js
// re-exports MODE_TYPES from here (as `MODE_TYPE`) instead of keeping its
// own copy. Keys are numbered per the design doc's own §6.2 list for
// traceability.
//
// Card #1794 (2026-08-20 pare-down, workstream `scoreboard`) — types 1, 2, 4,
// 6 (CLASS_SUMMARY_DETAILED, CLASS_SUMMARY_SIMPLE, MID_RIDE_SIMPLE,
// MID_CLASS_RESULT) were REMOVED. (MID_RIDE_SIMPLE was later re-added as a
// brand-new type by card #2483, and MID_CLASS_RESULT by card #2484, see their
// entries below; the two Class Summary types stay removed.) They are not
// renumbered away: the surviving
// keys below keep their original §6.2 numbers in their comments (3, 5, 7) so
// the gaps stay visible, because a stored ScoreboardView.modes[].modeType
// value referencing one of the four removed kebab-case strings can still
// exist in DynamoDB (dev/stage/main scanned on this card, finding stale rows:
// dev/main/demo one each, stage zero; see the card's own comment log — the
// fallback is load-bearing since stale rows do exist, as an already-existing
// one is as real as a future one). Renumbering would silently repoint a
// stored number at a different type; leaving a gap does not. Any config still
// naming a removed type resolves to no known layout family
// (layoutFamilyForModeType in score-fe) and therefore renders Blank, never a
// crash (#1798's universal-fallback rule) — see boardRenderState.js's
// UNKNOWN_MODE case, unchanged by this card, which already covered "modeType
// resolves to nothing" before this card existed.
export const MODE_TYPES = {
  RIDE_INTRO_DETAILED: "ride-intro-detailed", // §6.2 type 3
  POST_RIDE_SCORE: "post-ride-score", // §6.2 type 5
  END_OF_CLASS_RESULT: "end-of-class-result", // §6.2 type 7
  // Card #1795, the 2026-08-20 pare-down — Message: configured text (on the
  // mode itself, see MESSAGE_TEXT_MAX_LENGTH below), centred on an otherwise
  // empty board. §6.2's numbered list ran 1-7 and #1797 already added BLANK
  // OUTSIDE that numbering rather than renumbering the shipped 1-7
  // spellings (which are pinned by Cypress literals, see this file's own
  // header comment). This card's hard requirement 2 was "pick the next free
  // number and say why" — 8 is the next free number in that same §6.2
  // sequence (1-7 used, with 1/2/4/6 removed by #1794 and never reused), so
  // MESSAGE is numbered 8 even though it postdates and isn't itself part of
  // the original design doc's 7-item list, same footing BLANK already has
  // minus the explicit number. Deliberately absent from MODE_TYPE_FIELDS
  // (below) and SPONSOR_CAPABLE_MODE_TYPES, mirroring BLANK exactly:
  // Message's one piece of config (its text) lives directly on the mode,
  // resolved client-side, never through composeTypedFields/`fields` — it
  // performs no show/class/ride data lookup at all (card #1795 AC4), so
  // score-api never needs a case for it either.
  MESSAGE: "message", // #1795, next free number after §6.2's 1-7 = 8
  // Card #1797, the 2026-08-20 pare-down — Blank: a configured (or, absent
  // that, the Fox Village default) logo, centred on an otherwise empty
  // board. Not part of §6.2's original numbered 7; added outside that
  // numbering rather than renumbering the shipped 1-7 spellings above,
  // which are pinned by Cypress literals per this file's own header
  // comment. Deliberately absent from MODE_TYPE_FIELDS (below) and from
  // SPONSOR_CAPABLE_MODE_TYPES — Blank composes no fields at all (its one
  // piece of config, `logoAsset`, lives directly on the mode, resolved
  // client-side against score-fe's own small allow-list, never through
  // composeTypedFields) and is never sponsor/QR-capable. score-api's
  // composeTypedFields (scoreboard-public.js) already falls through to
  // `return undefined` for any modeType it doesn't explicitly compose, and
  // score-fe's modeTypes.js gates (sponsorVariantForModeType/
  // isQrCapableModeType) already fail closed for a type absent from their
  // maps — BLANK needs no new case in either place. It IS the universal
  // "nothing to show" fallback (#1798, Jaybill 2026-08-20: "NEVER show an
  // error message on the scoreboard"), so score-fe's BlankLayout renders it
  // from nothing more than this modeType + an optional logoAsset string.
  BLANK: "blank",
  // Card #2485 (Scoreboard 2.0, docs/score/scoreboard-2.0.md "Image mode and
  // mode groups") -- Image: one configured picture, with an optional caption,
  // on the fixed design canvas. Outside §6.2's 1-7 numbering for the same
  // reason BLANK and MESSAGE are (shipped spellings are pinned by Cypress
  // literals). Like MESSAGE/BLANK it is deliberately absent from
  // MODE_TYPE_FIELDS and SPONSOR_CAPABLE_MODE_TYPES: its config lives
  // directly on the mode (`imageAsset`, a brand-asset id, resolved by
  // score-api into `imageAssetUrl` on the public payload, and `caption`),
  // never through composeTypedFields, and it performs no show/class/ride
  // lookup at all. It is NON-DATA-DRIVEN (see NON_DATA_DRIVEN_MODE_TYPES).
  IMAGE: "image",
  // Card #2483 (Scoreboard 2.0, docs/score/scoreboard-2.0.md "Mode types",
  // spec type 4, Revision 1) -- During/Mid-Ride [Simple]: what is happening
  // in the ring RIGHT NOW (class number/name, test name, CNO, rider name and
  // status/division, horse name, QR to the class result page). A brand-new,
  // standalone type. Deliberately NOT folded into RIDE_INTRO_DETAILED: that
  // one is an AC7-frozen live mode (before the ride starts, wire value pinned
  // by Cypress literals #1639) and the two describe different moments of a
  // ride. The string "mid-ride-simple" was once a retired type's wire value
  // (card #1794 removed it). Measured fact (#1794 scan): 1 stored row each on
  // dev/main/demo carries it; those now render this type when selected.
  MID_RIDE_SIMPLE: "mid-ride-simple",
  // Card #2486 (Scoreboard 2.0, docs/score/scoreboard-2.0.md "Image mode and
  // mode groups") -- a MODE GROUP: an ordered, looping list of
  // `{ modeId, dwellSeconds }` members (`mode.members`), each naming ANOTHER
  // mode in the same view's modes[]. There is deliberately no separate
  // slideshow entity: a slideshow is a group of image modes. A group is a
  // container, never rendered itself: the public resolver turns it into its
  // currently active MEMBER (resolveModeGroupActive below) before anything
  // renders. Like MESSAGE/IMAGE/BLANK it is absent from MODE_TYPE_FIELDS and
  // SPONSOR_CAPABLE_MODE_TYPES. It is NOT in NON_DATA_DRIVEN_MODE_TYPES:
  // whether a group is default-eligible depends on its members
  // (isDefaultEligibleMode).
  MODE_GROUP: "mode-group",
  // Card #3217 (scoreboard-slideshow workstream) -- a SLIDESHOW mode: points at
  // an ORG-LEVEL slideshow record (`mode.slideshowId`, the score-slideshow
  // table), an ordered looping list of `{ imageAsset, dwellSeconds }` slides
  // shared by every show in the org. Unlike MODE_GROUP (members are other modes
  // in the SAME view, so never shareable across shows) the slides live outside
  // the view, so editing the slideshow once changes it on every show that uses
  // it. A container like a group: the public resolver turns it into its current
  // slide, shaped as an IMAGE mode (resolveSlideshowActive below), before
  // anything renders. Non-data-driven (reads no ride/ring data), so it is
  // default-eligible; absent from MODE_TYPE_FIELDS and SPONSOR_CAPABLE_MODE_TYPES.
  SLIDESHOW: "slideshow",
  // Card #2482 (Scoreboard 2.0, docs/score/scoreboard-2.0.md "Mode types", spec
  // types 1 and 2, Revision 1) -- Class Summary: ONE mode type carrying a
  // `fieldSet: "detailed" | "simple"` on the mode instance (FIELD_SETS below),
  // not two types. Neither spec type ever shipped, so there is no stored row to
  // migrate. NOTE: the strings "class-summary-detailed" / "class-summary-simple"
  // were the retired #1639 wire values (removed by #1794) and are deliberately
  // NOT accepted: this type's wire value is the bare "class-summary", and a
  // stale row naming a retired string still falls to Blank. Data-driven (reads
  // the class and its rides), so not in NON_DATA_DRIVEN_MODE_TYPES.
  CLASS_SUMMARY: "class-summary",
  // Card #2484 (Scoreboard 2.0, docs/score/scoreboard-2.0.md "Mode types",
  // spec type 6) -- Mid-Class Result: where a class stands while it is still
  // running. Completed rides by CURRENT RANK (computed from confirmed scores,
  // never from the stored ride.placing, which can legitimately be 0 and
  // means "unplaced"), banner "current class standings". A brand-new,
  // standalone type that REUSES the ranked-list family (and so its
  // by-division slideshow + combined layout + dwell), deliberately not folded
  // into END_OF_CLASS_RESULT: the wording rule differs ("rank", never
  // "place"/"placing"/"final"/"results"). The string "mid-class-result" was
  // once a retired type's wire value (card #1794 removed it); stored rows
  // carrying it render this type again when selected.
  MID_CLASS_RESULT: "mid-class-result",
};

// Card #2482 -- the closed enum a CLASS_SUMMARY mode's `fieldSet` may take.
// Same shape as RESULT_LAYOUTS (card #2314): a small operator-picked enum on
// the mode, never a free-form per-field picker (card #1799 stays closed).
// `detailed` = the class's start list; `simple` = a facts grid with no start
// list. Absent or unrecognised reads as DEFAULT_FIELD_SET (detailed, the
// richer of the two and the one whose rows the operator can see are
// populated), never an error and never an empty field list (#1798 doctrine).
export const FIELD_SETS = {
  DETAILED: "detailed",
  SIMPLE: "simple",
};

export const DEFAULT_FIELD_SET = FIELD_SETS.DETAILED;

// True when `value` is one of the two closed-enum members. `undefined` is NOT
// valid here (a save path treats absent as "unset" before asking); null is
// rejected too.
export function isValidFieldSet(value) {
  return Object.values(FIELD_SETS).includes(value);
}

// Reads a stored `fieldSet`: a valid member passes through, absent or anything
// unrecognised falls back to DEFAULT_FIELD_SET.
export function resolveFieldSet(value) {
  return isValidFieldSet(value) ? value : DEFAULT_FIELD_SET;
}

// Card #2487 -- the ring-truth transitions a View's optional `eventModeMap`
// may key on (docs/score/scoreboard-2.0.md "Event-driven switching"). There
// is no event bus: score-api fires one of these when a ring-truth write
// performs the transition. RIDE_START = a scribe opening a sheet moved the
// ring onto a different ride; RIDE_SCORED = a ride's sheet was completed and
// signed; CLASS_COMPLETE = a ring-truth write moved the ring OFF a class (a
// different class started, or the ring was set to BREAK / END OF DAY).
// Card #3431: BETWEEN_RIDES = the ring is between rides, the SAME determination
// announcer-fe's BETWEEN_RIDES state line makes (ring RUNNING with no live
// ride; see isRingBetweenRides in utils/ring-truth.js): it fires when the ring's
// ride reaches CONFIRMED/FINAL (the score is in and the horse has left) and when
// Score Admin sets the ring to BREAK. A break ROW on the daysheet is display-only
// and writes no ring state, so it is not a separate trigger: it sits inside the
// same determination (the ride before it is CONFIRMED/FINAL).
export const RING_EVENTS = {
  RIDE_START: "rideStart",
  RIDE_SCORED: "rideScored",
  CLASS_COMPLETE: "classComplete",
  BETWEEN_RIDES: "betweenRides",
};

export function isValidRingEvent(key) {
  return Object.values(RING_EVENTS).includes(key);
}

// Card #2488 (docs/score/scoreboard-2.0.md "Delayed class-complete switching").
// The classComplete eventModeMap entry may be a bare modeId string (default
// delay) or `{ modeId, classCompleteDelaySeconds }`. The delay exists so the
// last rider's post-ride-score screen is seen before the class result replaces
// it; settled default 30s, configurable per view. Only classComplete takes the
// object form.
export const DEFAULT_CLASS_COMPLETE_DELAY_SECONDS = 30;
export const MAX_CLASS_COMPLETE_DELAY_SECONDS = 3600;

/** The target modeId of an eventModeMap entry (string or object form), else null. */
export function eventModeMapTargetId(entry) {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object" && !Array.isArray(entry) && typeof entry.modeId === "string") {
    return entry.modeId;
  }
  return null;
}

/** True when `value` is an acceptable classCompleteDelaySeconds (0..max, finite). */
export function isValidClassCompleteDelaySeconds(value) {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= MAX_CLASS_COMPLETE_DELAY_SECONDS;
}

/** The delay for a view's eventModeMap: the classComplete entry's own field, else 30. */
export function resolveClassCompleteDelaySeconds(eventModeMap) {
  const entry = eventModeMap?.[RING_EVENTS.CLASS_COMPLETE];
  const own = entry && typeof entry === "object" ? entry.classCompleteDelaySeconds : undefined;
  return isValidClassCompleteDelaySeconds(own) ? own : DEFAULT_CLASS_COMPLETE_DELAY_SECONDS;
}

// Card #3373 (option B) -- how long the class result stays up when a class-to-class
// change armed it, before the board drops to the NEW class's live ride. Stored per
// view as `eventModeMap.classComplete.classResultDwellSeconds`; same rules as the
// delay (0..3600, finite number), settled default 30s. 0 skips the class result.
export const DEFAULT_CLASS_RESULT_DWELL_SECONDS = 30;
export const MAX_CLASS_RESULT_DWELL_SECONDS = 3600;

/** True when `value` is an acceptable classResultDwellSeconds (0..max, finite). */
export function isValidClassResultDwellSeconds(value) {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= MAX_CLASS_RESULT_DWELL_SECONDS;
}

/** The dwell for a view's eventModeMap: the classComplete entry's own field, else 30. */
export function resolveClassResultDwellSeconds(eventModeMap) {
  const entry = eventModeMap?.[RING_EVENTS.CLASS_COMPLETE];
  const own = entry && typeof entry === "object" ? entry.classResultDwellSeconds : undefined;
  return isValidClassResultDwellSeconds(own) ? own : DEFAULT_CLASS_RESULT_DWELL_SECONDS;
}

// Card #3565 (HITS Del Mar 2026-10-18) -- how long a rideScored screen (Post Ride Score)
// is kept up when the NEXT rider's rideStart arrives right behind it (a scribe opens the
// next sheet seconds after the last one signs). Stored per view as
// `eventModeMap.rideScored.rideScoredDwellSeconds` (the rideScored entry takes the object
// form `{ modeId, rideScoredDwellSeconds }`); same rules as the other delays (0..3600,
// finite number). 0 switches to the rideStart mode at once.
// The dwell runs from the rideScored switch, not from the rideStart. Default 20 s: the
// measured Del Mar gap between a rider's sheet completing and the next C sheet opening
// was 13 s, so anything at or under that (the first guess was 10) changes nothing on a
// real ring; 20 s keeps the score up through that gap with margin, yet still hands the
// board to Ride Intro before a normal scribe turnaround drags past half a minute.
export const DEFAULT_RIDE_SCORED_DWELL_SECONDS = 20;
export const MAX_RIDE_SCORED_DWELL_SECONDS = 3600;

/** True when `value` is an acceptable rideScoredDwellSeconds (0..max, finite). */
export function isValidRideScoredDwellSeconds(value) {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= MAX_RIDE_SCORED_DWELL_SECONDS;
}

/** The dwell for a view's eventModeMap: the rideScored entry's own field, else the default. */
export function resolveRideScoredDwellSeconds(eventModeMap) {
  const entry = eventModeMap?.[RING_EVENTS.RIDE_SCORED];
  const own = entry && typeof entry === "object" ? entry.rideScoredDwellSeconds : undefined;
  return isValidRideScoredDwellSeconds(own) ? own : DEFAULT_RIDE_SCORED_DWELL_SECONDS;
}

function instantDue(now, notBeforeAt) {
  const notBefore = Date.parse(notBeforeAt);
  if (!Number.isFinite(notBefore)) return false;
  const nowMs = now instanceof Date ? now.getTime() : typeof now === "number" ? now : Date.parse(now);
  return Number.isFinite(nowMs) && nowMs >= notBefore;
}

/**
 * Card #3560: ms from `now` until a stage's `notBeforeAt` (negative once due),
 * or null when either instant is unparseable. Lives beside instantDue so the
 * notBeforeAt arithmetic exists in this one file (the AC3 'one implementation'
 * guard); score-api's clock-change.js uses it to tell a board how long until
 * the clock changes what it would compose.
 */
export function msUntilNotBefore(now, notBeforeAt) {
  const notBefore = Date.parse(notBeforeAt);
  const nowMs = now instanceof Date ? now.getTime() : typeof now === "number" ? now : Date.parse(now);
  return Number.isFinite(notBefore) && Number.isFinite(nowMs) ? notBefore - nowMs : null;
}

function pendingSwitchDue(now, pendingModeSwitch) {
  if (!pendingModeSwitch || typeof pendingModeSwitch.modeId === "undefined") return false;
  return instantDue(now, pendingModeSwitch.notBeforeAt);
}

// Card #3373: a pendingModeSwitch may carry a second stage,
// `then: { modeId, notBeforeAt }`: once that instant passes the board moves on
// from the (class result) stage to this mode (the new class's live ride).
function pendingThenDue(now, pendingModeSwitch) {
  const then = pendingModeSwitch?.then;
  if (!then || typeof then.modeId !== "string") return false;
  return pendingSwitchDue(now, pendingModeSwitch) && instantDue(now, then.notBeforeAt);
}

/**
 * Card #3373: the pendingModeSwitch with its second stage set to `modeId`, due
 * `dwellSeconds` after the first stage's notBeforeAt (the class result's own
 * dwell). `modeId` equal to the first stage's (or null) drops the second stage.
 * Pure: never touches the clock.
 */
export function withPendingThenStage(pendingModeSwitch, modeId, dwellSeconds) {
  const { then: _drop, ...first } = pendingModeSwitch;
  const firstAt = Date.parse(first.notBeforeAt);
  if (typeof modeId !== "string" || modeId === first.modeId || !Number.isFinite(firstAt)) return first;
  return { ...first, then: { modeId, notBeforeAt: new Date(firstAt + dwellSeconds * 1000).toISOString() } };
}

/**
 * The mode a pendingModeSwitch finally settles on once every stage is due
 * (`then.modeId` when it has a second stage, else `modeId`), or null if none.
 * Lets a poller know when to stop ticking (card #3373).
 */
export function pendingSwitchFinalModeId(pendingModeSwitch) {
  if (!pendingModeSwitch || typeof pendingModeSwitch.modeId === "undefined") return null;
  const then = pendingModeSwitch.then;
  return then && typeof then.modeId === "string" ? then.modeId : pendingModeSwitch.modeId;
}

/**
 * THE one implementation of a view's effective current mode (card #2488), a
 * pure function of wall-clock time and stored state: nothing keeps time, so
 * the switch lands with no browser open. The board's public route, the remote
 * and any screen resolver all call this; never re-derive it.
 *   pendingModeSwitch && now >= pendingModeSwitch.notBeforeAt
 *     ? pendingModeSwitch.modeId : currentModeId
 */
export function resolveEffectiveModeId(now, currentModeId, pendingModeSwitch) {
  if (pendingThenDue(now, pendingModeSwitch)) return pendingModeSwitch.then.modeId;
  return pendingSwitchDue(now, pendingModeSwitch) ? pendingModeSwitch.modeId : currentModeId ?? null;
}

/**
 * The mode-group loop origin that goes with resolveEffectiveModeId: a group
 * that became current by a due pending switch starts counting at notBeforeAt
 * (the instant it took over), not at whenever the stored view was written.
 */
export function resolveEffectiveModeGroupStartedAt(now, modeGroupStartedAt, pendingModeSwitch) {
  if (pendingThenDue(now, pendingModeSwitch)) return pendingModeSwitch.then.notBeforeAt;
  return pendingSwitchDue(now, pendingModeSwitch) ? pendingModeSwitch.notBeforeAt : modeGroupStartedAt ?? null;
}

// Card #2485 -- the explicit, queryable "non-data-driven" category the
// Scoreboard 2.0 `defaultModeId` card consumes (docs/score/scoreboard-2.0.md:
// a View's default may only be a mode that reads no ride or ring data, and a
// mode group may only be default-eligible when every member is one of
// these). A mode type is non-data-driven when rendering it needs NOTHING from
// the show/class/ride/ring tables, only config stored on the mode itself.
// Membership is an allow-list, so a type nobody classified (a retired one
// still stored on an old view, a future one) is data-driven by default:
// fails closed, never accidentally default-eligible.
export const NON_DATA_DRIVEN_MODE_TYPES = [
  MODE_TYPES.MESSAGE,
  MODE_TYPES.IMAGE,
  MODE_TYPES.BLANK,
  MODE_TYPES.SLIDESHOW,
];

export function isNonDataDrivenModeType(modeType) {
  return NON_DATA_DRIVEN_MODE_TYPES.includes(modeType);
}

// Card #3305 -- the single-rider screens: the mode types that show ONE rider
// (ride intro, mid-ride, post-ride score). A view-level ride pick on the
// scoreboard remote (View.ridePick) drives every one of these and none of the
// class-level (class result, end of class, class summary) or non-ride types.
// An allow-list for the same fail-closed reason as the one above: a type
// nobody classified (a retired one, a future one) ignores the pick until
// someone decides it is single-rider.
export const SINGLE_RIDER_MODE_TYPES = [
  MODE_TYPES.RIDE_INTRO_DETAILED,
  MODE_TYPES.MID_RIDE_SIMPLE,
  MODE_TYPES.POST_RIDE_SCORE,
];

export function isSingleRiderModeType(modeType) {
  return SINGLE_RIDER_MODE_TYPES.includes(modeType);
}

// Card #2490 -- a View's nullable `defaultModeId` names the mode rendered when
// the view has no current mode (or it was cleared), instead of the hardcoded
// Blank. It may ONLY point at a mode that is default-eligible: a default must
// never read ride or ring data (docs/score/scoreboard-2.0.md), so the test is
// the single non-data-driven predicate above, never a second list.
// Card #2486 extended it (this is the ONE check, not a second one): a MODE
// GROUP is eligible iff it has at least one PRESENT member and EVERY present
// member is a non-group, non-data-driven mode in `modes` (the view's
// modes[]). A member naming no mode (deleted out from under the group) or
// another group is IGNORED, exactly as resolveModeGroupActive skips it at
// read time, so deleting a member never strands the default; a group with no
// present member at all is not eligible. A non-group mode is eligible iff its
// type is non-data-driven, exactly as before, and needs no `modes`. Fails
// closed on a missing/odd mode.
export function isDefaultEligibleMode(mode, modes) {
  if (mode?.modeType === MODE_TYPES.MODE_GROUP) {
    if (!Array.isArray(mode.members) || !Array.isArray(modes)) return false;
    const present = mode.members
      .map((member) => modes.find((m) => m && m.id === member?.modeId))
      .filter((target) => target && target.modeType !== MODE_TYPES.MODE_GROUP);
    return present.length > 0 && present.every((target) => isNonDataDrivenModeType(target.modeType));
  }
  return isNonDataDrivenModeType(mode?.modeType);
}

// Resolves a view's `defaultModeId` against its modes[]: the mode when the id
// names a present, default-eligible mode, else null (unset, deleted, or a
// stale id now pointing at a data-driven type). The public board uses this at
// READ time so a dangling default degrades to today's Blank, never an error.
export function resolveDefaultMode(modes, defaultModeId) {
  if (!defaultModeId || !Array.isArray(modes)) return null;
  const mode = modes.find((m) => m && m.id === defaultModeId);
  return isDefaultEligibleMode(mode, modes) ? mode : null;
}

// Card #2486 -- mode-group timing. A group becomes current by the View
// stamping `modeGroupStartedAt` (an ISO timestamp). Every reader (the public
// board's resolver, the remote) derives the active member as a PURE function
// of wall-clock time and stored config; nothing runs between reads, so the
// loop advances with no browser open anywhere. If implementing this ever
// needs a tab, a timer, a cron or another Lambda, it is wrong. This is the
// ONE wall-clock implementation. Dwell BOUNDS are shared with card #2332 (it
// shipped first): a member's dwellSeconds validates with the same
// isValidResultDwellSeconds / RESULT_DWELL_SECONDS_MIN..MAX below, there is no
// second set. #2332's DivisionSlideshow rotates PAGES inside one mode's own
// result list on a browser interval; that is a different concern from looping
// BETWEEN modes with no tab open, so it does not use this function.
//
// Dwell origin for a group that was NEVER made current (it is the View's
// `defaultModeId`, so no modeGroupStartedAt was ever stamped): the Unix
// epoch. Every reader agrees on it with no stored state, and the position is
// still a pure function of now + config.
export const MODE_GROUP_DEFAULT_ORIGIN_MS = 0;

// Member dwell bounds are card #2332's (RESULT_DWELL_SECONDS_MIN/MAX and
// isValidResultDwellSeconds, defined with resolveResultLayout below): writes
// are validated with them. The READER stays tolerant of a stored 0 or junk: it
// skips such a member, and a group whose members all total 0 resolves to Blank,
// never a divide by zero.

function toEpochMs(value, fallbackMs) {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const ms = Date.parse(value);
    if (Number.isFinite(ms)) return ms;
  }
  return fallbackMs;
}

// The active member of `group` at `now`, or null when the group has nothing
// to show (no usable members: every member deleted/unknown/itself a group, or
// the dwell seconds sum to zero). Callers render null as Blank.
//   group:     the mode-group mode ({ members: [{ modeId, dwellSeconds }] })
//   modes:     the view's modes[] (members are looked up here by id)
//   startedAt: the view's modeGroupStartedAt (ISO/Date/ms); null/invalid uses
//              MODE_GROUP_DEFAULT_ORIGIN_MS
//   now:       Date / ms / ISO
// Returns { mode, member, index, msIntoMember, msUntilNext, cycleMs } where
// `index` is the position in the STORED group.members (skipped members still
// count, so it always names the member's real row). A member's window is
// [offset, offset + dwell): the start instant belongs to it, and the instant
// of the wrap (elapsed an exact multiple of the cycle) is the first member.
// A startedAt in the future (clock skew) clamps to the first member.
export function resolveModeGroupActive(group, modes, startedAt, now) {
  if (!group || !Array.isArray(group.members) || !Array.isArray(modes)) return null;
  const usable = [];
  for (let memberIndex = 0; memberIndex < group.members.length; memberIndex += 1) {
    const member = group.members[memberIndex];
    const dwellMs = Math.round(Number(member?.dwellSeconds) * 1000);
    if (!Number.isFinite(dwellMs) || dwellMs <= 0) continue;
    const mode = modes.find((m) => m && m.id === member?.modeId);
    if (!mode || mode.modeType === MODE_TYPES.MODE_GROUP) continue;
    usable.push({ member, mode, dwellMs, memberIndex });
  }
  const cycleMs = usable.reduce((sum, u) => sum + u.dwellMs, 0);
  if (usable.length === 0 || cycleMs <= 0) return null;
  const nowMs = toEpochMs(now, Date.now());
  const elapsed = Math.max(0, nowMs - toEpochMs(startedAt, MODE_GROUP_DEFAULT_ORIGIN_MS));
  const position = elapsed % cycleMs;
  let offset = 0;
  for (let index = 0; index < usable.length; index += 1) {
    const { member, mode, dwellMs, memberIndex } = usable[index];
    if (position < offset + dwellMs) {
      return { mode, member, index: memberIndex, msIntoMember: position - offset, msUntilNext: offset + dwellMs - position, cycleMs };
    }
    offset += dwellMs;
  }
  return null;
}

// Card #3217 -- the slide a SLIDESHOW is showing at `now`, or null when it has
// nothing to show (no slides, or every dwell 0/junk). Callers render null as
// Blank. This is NOT a second clock implementation: the slides are wrapped
// into a synthetic mode group over synthetic IMAGE modes and handed to
// resolveModeGroupActive, so a slideshow and a mode group agree to the
// millisecond on the boundaries (start instant belongs to the slide, wrap is
// slide 0, future startedAt clamps to slide 0, unusable dwell skipped).
//   slideshow: { slides: [{ imageAsset, dwellSeconds }] }
//   startedAt: the view's modeGroupStartedAt (stamped when the slideshow mode
//              becomes current); null/invalid counts from the shared origin
// Returns { slide, index, msIntoSlide, msUntilNext, cycleMs } where `index` is
// the position in the STORED slides.
export function resolveSlideshowActive(slideshow, startedAt, now) {
  if (!slideshow || !Array.isArray(slideshow.slides)) return null;
  const modes = slideshow.slides.map((slide, i) => ({
    id: `slide-${i}`,
    modeType: MODE_TYPES.IMAGE,
    imageAsset: slide?.imageAsset ?? null,
  }));
  const group = {
    members: slideshow.slides.map((slide, i) => ({ modeId: `slide-${i}`, dwellSeconds: slide?.dwellSeconds })),
  };
  const active = resolveModeGroupActive(group, modes, startedAt, now);
  if (!active) return null;
  return {
    slide: slideshow.slides[active.index],
    index: active.index,
    msIntoSlide: active.msIntoMember,
    msUntilNext: active.msUntilNext,
    cycleMs: active.cycleMs,
  };
}

// Card #3217 -- a new slide's default dwell. The 3..120 whole-second bounds are
// card #2332's (isValidResultDwellSeconds, one set).
export const SLIDESHOW_DWELL_SECONDS_DEFAULT = 5;

// Card #3314 -- an Image Loop's banner setting: what happens to the footer
// banner (clock bar) while one of its slides is on the board.
//   none:    the banner is not drawn; the slide gets the whole canvas.
//   below:   the banner is drawn as today; the slide gets the space above it.
//            The DEFAULT, and what a loop saved before this card reads as.
//   overlay: the slide gets the whole canvas and the banner is drawn over its
//            bottom edge.
export const SLIDESHOW_BANNER_MODES = {
  NONE: "none",
  BELOW: "below",
  OVERLAY: "overlay",
};
export const SLIDESHOW_BANNER_MODE_DEFAULT = SLIDESHOW_BANNER_MODES.BELOW;

export function isValidSlideshowBannerMode(value) {
  return Object.values(SLIDESHOW_BANNER_MODES).includes(value);
}

// A stored/received value read into one valid mode; absent or junk is the
// default (the board never throws on a stored shape).
export function resolveSlideshowBannerMode(value) {
  return isValidSlideshowBannerMode(value) ? value : SLIDESHOW_BANNER_MODE_DEFAULT;
}

// Card #3308 -- scoreboard screen transitions. A transition is
// `{ type, durationMs }`:
//   cut:       swap instantly (no animation).
//   fade:      the old screen fades out to the theme background, then the new
//              one fades in. The DEFAULT. durationMs is each way.
//   crossfade: old and new overlap, one fading out while the other fades in.
//              durationMs is the whole overlap.
// A theme carries one (absent/null reads as the default fade, 500ms); a
// slideshow may carry its own for slide-to-slide changes (absent/null inherits
// the theme's). Only the content area above the footer bar ever transitions.
export const SCOREBOARD_TRANSITION_TYPES = {
  CUT: "cut",
  FADE: "fade",
  CROSSFADE: "crossfade",
};
export const DEFAULT_TRANSITION_TYPE = SCOREBOARD_TRANSITION_TYPES.FADE;
export const DEFAULT_TRANSITION_DURATION_MS = 500;
export const TRANSITION_DURATION_MS_MIN = 100;
export const TRANSITION_DURATION_MS_MAX = 3000;

export function isValidTransitionType(value) {
  return Object.values(SCOREBOARD_TRANSITION_TYPES).includes(value);
}

export function isValidTransitionDurationMs(value) {
  return (
    Number.isInteger(value) && value >= TRANSITION_DURATION_MS_MIN && value <= TRANSITION_DURATION_MS_MAX
  );
}

// True for a storable transition: a closed-enum type, and (when present) an
// in-bounds whole-millisecond duration. A "cut" has no duration to validate
// but may still carry one. `undefined`/`null` are NOT valid here (callers
// treat those as "unset" before asking).
export function isValidTransition(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  if (!isValidTransitionType(value.type)) return false;
  return value.durationMs === undefined || isValidTransitionDurationMs(value.durationMs);
}

// The storable form of a VALID transition (callers validate first): only the
// two known fields, durationMs filled with the default when omitted. null stays
// null ("unset": theme default / inherit the theme).
export function normalizeTransition(value) {
  if (value === null || value === undefined) return null;
  return { type: value.type, durationMs: value.durationMs ?? DEFAULT_TRANSITION_DURATION_MS };
}

// Reads possibly-absent/junk stored transitions into one concrete
// `{ type, durationMs }`. The slideshow's own wins when valid, then the
// theme's, then the default fade. Each source is checked as a whole, so a
// half-valid one (good type, junk duration) falls through to the next rather
// than yielding a surprise value. Never throws: the board must keep working
// on any stored shape.
export function resolveTransition(themeTransition, slideshowTransition) {
  const pick = [slideshowTransition, themeTransition].find(isValidTransition);
  if (!pick) {
    return { type: DEFAULT_TRANSITION_TYPE, durationMs: DEFAULT_TRANSITION_DURATION_MS };
  }
  return { type: pick.type, durationMs: pick.durationMs ?? DEFAULT_TRANSITION_DURATION_MS };
}

// Card #2489 (Scoreboard 2.0, per-mode control) -- the per-mode watermark
// IMAGERY toggle. Each mode instance (ScoreboardView.modes[]) may carry
// `watermarkMode`, a CLOSED enum of exactly these three values:
//   inherit: do whatever the theme says (theme.watermark.url present means
//            the layer renders, absent means it does not). The default, so a
//            mode that never touches the field renders exactly as before.
//   show:    render the theme's watermark for this mode. A NO-OP when the
//            theme declares no watermark url (there is no imagery to show;
//            a mode cannot conjure an image the theme never set), so it
//            renders the same as inherit today. Kept as its own value so an
//            operator's explicit "always show" survives a future change to
//            what inherit means.
//   hide:    suppress the theme watermark for this mode only.
// Absent/unset reads as `inherit` (DEFAULT_WATERMARK_MODE), so no stored row
// needs a migration. NAMING COLLISION: this is the IMAGERY meaning of
// "watermark"; the unrelated payload-cache invalidation signal is score-api's
// per-show change signal (lib/change-signal.js, #3558) and is untouched by
// this.
export const WATERMARK_MODES = {
  INHERIT: "inherit",
  SHOW: "show",
  HIDE: "hide",
};

export const DEFAULT_WATERMARK_MODE = WATERMARK_MODES.INHERIT;

// True when `value` is one of the three closed-enum members. `undefined` is
// NOT valid here (callers treat absent as "unset" before asking); use
// resolveWatermarkMode to read a possibly-absent stored value.
export function isValidWatermarkMode(value) {
  return Object.values(WATERMARK_MODES).includes(value);
}

// Reads a stored `watermarkMode`: a valid member passes through, absent or
// anything unrecognised falls back to inherit (fails closed to today's
// behaviour, never to a surprising hide/show).
export function resolveWatermarkMode(value) {
  return isValidWatermarkMode(value) ? value : DEFAULT_WATERMARK_MODE;
}

// Card #2491 (Scoreboard 2.0, per-mode control) -- every mode instance
// (ScoreboardView.modes[]) may carry `visibleOnRemote`, a boolean that
// defaults to true. `false` hides the mode's button from the remote's MANUAL
// mode-button list only; the mode stays fully functional (selectable by a
// direct setScoreboardViewCurrentMode call or by event-driven switching, and
// the board renders it as normal). Absent reads as true, so no stored row
// needs a migration. Only a strict boolean false hides: a missing, null or
// otherwise odd stored value fails SAFE to visible (a stray value must never
// make an operator's button vanish).
export const DEFAULT_VISIBLE_ON_REMOTE = true;

// True when `value` is a real boolean. `undefined` is NOT valid here
// (callers treat absent as "unset" before asking); null is rejected too.
export function isValidVisibleOnRemote(value) {
  return typeof value === "boolean";
}

// Reads a mode's stored `visibleOnRemote`: only an explicit `false` hides.
export function isModeVisibleOnRemote(mode) {
  const stored = mode?.visibleOnRemote;
  return typeof stored === "boolean" ? stored : DEFAULT_VISIBLE_ON_REMOTE;
}

// Card #2485 -- ceiling for the optional Image-mode caption (the remote's
// `maxLength`). One line of large type under the picture, not a paragraph:
// anything longer belongs in a Message mode.
export const IMAGE_CAPTION_MAX_LENGTH = 120;

// Card #2314 — per-CLASS result-layout choice for the END_OF_CLASS_RESULT
// view, offered from the scoreboard remote. DECIDED BY JAYBILL 2026-09-05:
// operator-picked, never auto-detected (a heuristic that guesses wrong on a
// live board can't be corrected). This is deliberately a per-class layout
// choice on the one existing view type, not a new MODE_TYPE and not general
// field config (see docs/score/scoreboard-design.md's #2314 revision entry,
// which records this as a deliberate revision of the earlier "five view
// types, no field configuration" call).
//
// BY_DIVISION is today's existing RankedListLayout behaviour (grouped by
// entryIdentity.riderStatus, #1945) and is both the DEFAULT and the fallback
// (AC4): stored on `Class.resultLayout`, an unset or unrecognised value must
// degrade to BY_DIVISION, never an error and never a blank board — same
// fails-closed doctrine as isKnownTermReason below. COMBINED renders the
// class's whole ranked list as one table, no DIVISION heading, regardless of
// whether the data carries a riderStatus.
export const RESULT_LAYOUTS = {
  BY_DIVISION: "by-division",
  COMBINED: "combined",
};

export function isKnownResultLayout(value) {
  return Object.values(RESULT_LAYOUTS).includes(value);
}

// AC4 — the single fails-closed resolver every reader of a stored
// Class.resultLayout must go through, so "unset" and "unrecognised" collapse
// to the exact same default rather than each call site re-deciding.
export function resolveResultLayout(value) {
  return isKnownResultLayout(value) ? value : RESULT_LAYOUTS.BY_DIVISION;
}

// Card #2332 — by-division now means a looping slideshow, one division at a
// time. The per-division dwell is operator-settable (stored on
// `Class.resultDwellSeconds`, set from the remote's ModeEditorDialog, same
// shape as resultLayout above). Fails closed exactly like resolveResultLayout
// (scoreboard doctrine #1798): an unset, non-numeric, non-integer or
// out-of-range stored value collapses to the 10s default, never an error and
// never a blank board. The range is a sanity bound: under 3s a division flips
// before a projector audience can read it, over 120s the slideshow is
// indistinguishable from a stuck board.
export const RESULT_DWELL_SECONDS_DEFAULT = 10;
export const RESULT_DWELL_SECONDS_MIN = 3;
export const RESULT_DWELL_SECONDS_MAX = 120;

export function isValidResultDwellSeconds(value) {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= RESULT_DWELL_SECONDS_MIN &&
    value <= RESULT_DWELL_SECONDS_MAX
  );
}

export function resolveResultDwellSeconds(value) {
  return isValidResultDwellSeconds(value) ? value : RESULT_DWELL_SECONDS_DEFAULT;
}

// #1795 — the character cap the remote's Message text field enforces
// (`maxLength`), and the ceiling MessageLayout.jsx's length-tiered font-size
// scaling was sized against. Decision recorded on the card and in the
// ModeEditorDialog/MessageLayout commit: wrap-and-scale-to-a-floor, backed by
// this hard cap as the second layer, rather than either alone — a pure
// character cap with no scaling would still let a merely-long-but-under-cap
// message overflow at full size, and pure JS-measured "shrink until it fits"
// was rejected as fragile on kiosk hardware (font-loading races) and gives
// the remote operator no signal of what's too long until it's already live
// on the board. Ellipsis truncation was also rejected: silently cutting an
// operational message ("Ring 1 resumes at 1:30 — please...") can drop the
// actionable half of the sentence, worse than the operator having to shorten
// it up front. 240 is generous enough for a couple of short sentences while
// keeping every tier's floor font-size still "by far the largest type on the
// board" (card #1795 AC3).
export const MESSAGE_TEXT_MAX_LENGTH = 240;

// design §6.2 — the exact field-key list per mode type. Per §6.1's shared
// EntryIdentity component, `entryIdentity` bundles CNO + riderName +
// riderStatus + horseName as ONE field, not four. `startList`/`rankedList`
// are likewise single fields bundling their per-row shape (chrono row /
// ranked row respectively) — the design offers "the list", not its
// constituent columns. `banner` (types 6/7's fixed "current class standings"
// / "final class results" copy) is NOT listed here — it is inherent to the
// type, never toggled.
//
// Card #1799 (2026-08-20, "kill per-mode field selection") — this stopped
// being PICKER vocabulary. There is no more per-view `fields[]` selection:
// a view of a given mode type renders EVERY field its type defines, always.
// This constant is now simply the field set of the type — the exhaustive
// list score-api's composeSelectedFields (lib/scoreboard-mode-payload.js)
// composes from, unconditionally, whenever the underlying data resolves. A
// key still ends up absent from the composed payload only when its
// candidate value doesn't exist for this class/entry (design §1's "missing
// data is omitted, never placeholdered" — unchanged by this card), never
// because a view chose not to select it. A stored ScoreboardView.modes[]
// row may still carry a `fields[]` array from before this card (existing
// dev/stage/main rows may); it is simply ignored on read rather than
// migrated/stripped, since #1799 was framed as "for now," which argues for
// reversibility over destroying the data — see the card's own decision
// log. Two of the keys below are structurally unproducible by
// composeSelectedFields and are never emitted under a `fields` key at all:
// `sponsor` lives in the envelope (`sponsor` at the top level of
// getPublicScoreboard's response, gated by isSponsorCapable below, never
// inside `payload.fields`), and `qr` is resolved client-side (design §6.3)
// with nothing for the server to compose. For types listing these keys
// (POST_RIDE_SCORE and END_OF_CLASS_RESULT), a composed `fields` object's
// key set is always a SUBSET of this list for the type, never equal to it.
// RIDE_INTRO_DETAILED has no unproducible keys, so its composed set can
// equal the type's list. Assert accordingly when testing composeSelectedFields's
// output against this constant.
export const MODE_TYPE_FIELDS = {
  [MODE_TYPES.RIDE_INTRO_DETAILED]: [
    "classNumberName",
    "testName",
    "entryIdentity",
    "riderLocation",
    "horseBio",
    "ownerInfo",
    // Card #2294 -- the class's judging panel for p4.html's JUDGES line. Same
    // shape and composer as END_OF_CLASS_RESULT's: an array of
    // {position, judges: [name, ...]} rows in arena order (E, H, C, M, B),
    // names only. Omitted when no judge is assigned to the class.
    "judgesPositions",
  ],
  [MODE_TYPES.POST_RIDE_SCORE]: [
    "classNumberName",
    "testName",
    // Card #2294 -- the class's judging panel for p5.html's JUDGES line (the
    // only thing on that board saying which judge the breakout's letters
    // belong to). Same shape and composer as RIDE_INTRO_DETAILED's.
    "judgesPositions",
    "entryIdentity",
    "scoreHero",
    // Card #2312 — the DR123.3 outcome (Scratched/Excused/Eliminated/No
    // Show) for a terminated ride, same field name and same score-common
    // read path #2060/#2104 already put on RankedList/ClassResultsPage.
    // This is the one type those two cards left uncovered: the post-ride
    // single-entry screen had no way to show the outcome, so a terminated
    // ride's composite-0 scoreHero either printed "0.000%" (pre-#2285) or,
    // after #2285's below-55 suppression, dropped the whole right column
    // with nothing in its place. Composed only when a recognised term
    // reason resolves (see isKnownTermReason, score-common/utils/
    // ride-status.js) — never a fifth vocabulary, never the legacy #916
    // `ride.termReason` wire field.
    "termReason",
    "currentRank",
    // Card #2282 — division-scoped sibling to currentRank, same {rank,
    // ofCount} shape, composed only when the ride's division resolves.
    "divisionRank",
    "perJudgeBreakout",
    "qr",
  ],
  [MODE_TYPES.END_OF_CLASS_RESULT]: [
    "classNumberName",
    "judgesPositions",
    "rankedList",
    // Card #2314 — the per-class layout choice (RESULT_LAYOUTS above),
    // always resolved (never absent) via resolveResultLayout, so this key
    // is the one exception to this list's "subset, not equal" comment above
    // for this type: it's always present once composeEndOfClassResult runs.
    "resultLayout",
    // Card #2332 — per-division dwell (seconds) for the by-division
    // slideshow, always resolved via resolveResultDwellSeconds like
    // resultLayout, so always present once composeEndOfClassResult runs.
    "resultDwellSeconds",
    "sponsor",
    "qr",
  ],
  // Card #2484 -- the spec's field list is class number, class name, judge
  // name(s) and arena position, the ranked rows (per row current rank, CNO,
  // rider name, rider status/division, horse name), sponsor and QR.
  // `resultLayout` / `resultDwellSeconds` are the same per-class presentation
  // choices END_OF_CLASS_RESULT carries (always resolved fail-closed by
  // composeMidClassResult). Unlike END_OF_CLASS_RESULT, a row never carries
  // `placing`: only `rank` (index + 1 of the confirmed-score sort).
  [MODE_TYPES.MID_CLASS_RESULT]: [
    "classNumberName",
    "judgesPositions",
    "rankedList",
    "resultLayout",
    "resultDwellSeconds",
    "sponsor",
    "qr",
  ],
  // Card #2483 -- the spec's field list is class number, class name, test
  // name, CNO, rider name, rider status/division, horse name and the QR.
  // score-api composes them as the same bundles every other type uses:
  // classNumberName (number + name), testName, entryIdentity (CNO, rider
  // name, status/division, horse name). `qr` is resolved client-side, never
  // emitted under `fields` (same as POST_RIDE_SCORE).
  [MODE_TYPES.MID_RIDE_SIMPLE]: ["classNumberName", "testName", "entryIdentity", "qr"],
  // Card #2482 -- the ONE entry keyed by fieldSet instead of a flat array (use
  // score-api's allowedFieldsFor(modeType, fieldSet), never index this
  // directly). Each list is exactly the spec's field list for that field set.
  // `startList` bundles the per-row ride time + CNO + rider name + rider
  // status/division + horse name (one field, like rankedList). `resultLayout`
  // and `resultDwellSeconds` are the by-division presentation settings reused
  // from END_OF_CLASS_RESULT (#2314/#2332), always resolved, so present
  // whenever the start list is composed. `sponsor` lives in the envelope and
  // `qr` is resolved client-side, so neither is ever emitted under `fields`.
  // Judge home city/state/country is deliberately absent: it does not exist in
  // cloud data (v1 section 9) and is neither invented nor rendered blank.
  // detailed is NOT a superset of simple: simple drops the start list and adds
  // test name, class start/estimated finish, ride count and ring.
  [MODE_TYPES.CLASS_SUMMARY]: {
    [FIELD_SETS.DETAILED]: [
      "judgesPositions",
      "classNumberName",
      "startList",
      "resultLayout",
      "resultDwellSeconds",
      "sponsor",
      "qr",
    ],
    [FIELD_SETS.SIMPLE]: [
      "classNumberName",
      "testName",
      "judgesPositions",
      "classStart",
      "estimatedFinish",
      "totalRides",
      "ring",
      "sponsor",
      "qr",
    ],
  },
  // MODE_TYPES.BLANK is intentionally NOT a key here. Every lookup against
  // this map already falls back to [] for a missing key (score-api's
  // allowedFieldsFor, `MODE_TYPE_FIELDS[modeType] || []`), which is exactly
  // right — its one piece of config (`logoAsset`) lives directly on the mode
  // object, not inside `fields`.
};

// design §7.1 originally listed 4 sponsor-capable types (the summary/result
// families); card #1794 removes 3 of them (CLASS_SUMMARY_DETAILED,
// CLASS_SUMMARY_SIMPLE, MID_CLASS_RESULT all left MODE_TYPES entirely), so
// END_OF_CLASS_RESULT is the sole survivor. Every other type is still nulled
// server-side rather than trusting the client to ignore it ("the server
// composes, it never trusts the client to enforce a rule").
// Card #2482 adds CLASS_SUMMARY: "sponsor name, if one is declared" is in both
// of its field sets (rendered as a text line by score-fe's ClassSummaryLayout).
//
// Card #2484 re-adds MID_CLASS_RESULT as a sponsor-capable type ("sponsor name
// if declared", spec type 6), the second ranked-list member.
export const SPONSOR_CAPABLE_MODE_TYPES = [
  MODE_TYPES.END_OF_CLASS_RESULT,
  MODE_TYPES.CLASS_SUMMARY,
  MODE_TYPES.MID_CLASS_RESULT,
];

// #1755 — lifted out of scribe-fe's ScoringForm.jsx (#499 freestyle
// Technical/Artistic, #542 young-horse Technical/Quality), so any FE
// grouping a test's movements by section (scribe's ScoringForm AND
// scoreadmin-fe's RideEditor) renders the identical STATIC i18n key for a
// given section value instead of each surface keeping its own copy that
// can drift. Each app's own locale bundle still owns the translated text
// at this key path (both currently resolve it under a top-level "scoring"
// namespace) — this map only pins which key a section maps to.
export const FREESTYLE_SECTION_LABEL_KEY = {
  technical: "scoring.technicalExecution",
  artistic: "scoring.artisticImpression",
  // #542 — young-horse / Children tests split technical vs quality; label
  // the quality section header when it renders.
  quality: "scoring.qualityImpression",
};

// #1756 - lifted from scribe-fe's ScoringForm.jsx (#208, split by #753),
// which is where these strings were authored, and where scoreadmin-fe's
// RideEditor had a hand-mirrored second copy with a comment saying so.
// Two copies of a preset list is two lists: the scribe and the admin
// eliminate a ride for "Rider Fall" only for as long as nobody edits one
// file and not the other.
//
// Both scoreadmin-fe's RideEditor (#1756) and scribe-fe's ScoringForm (#1807)
// render THIS array, so the two menus cannot drift. It is the one copy to
// edit.
//
// These are the reasons a human picks from, not a rule schedule: none of
// them carries a figure, and picking one never computes a deduction. The
// error/deduction FIGURES live in scoring.js (SCHEME_TIERS,
// FREESTYLE_ERROR_TIERS), cited to USEF DR122.5 there. An eliminate-tier
// error auto-eliminates (DR122.5.b.3) — scribe-fe/scoreadmin-fe push a
// fromError:true eliminations[] entry themselves when a tier resolves to
// one (#1831, revisiting the #682 2026-07-15 MVP deferral); this array is
// only the reasons a human picks by hand for every OTHER elimination cause.
//
// Neither surface renders these through i18n today (scribe-fe's #208 menu
// and RideEditor's old copy were both raw English strings); keeping them
// raw here is a like-for-like lift, not a new decision. The free-text
// "Custom reason…" fallback each surface already offers is what covers a
// reason this list does not.
// #1992 — "No Show", raised only by the scribe scoresheet's presiding-judge
// (position C) action, which waterfalls it onto the ride as a whole rather
// than one judge's own sheet (see score-api's ride.js markRideNoShow). Kept
// as its OWN named export, deliberately NOT a member of ELIMINATION_REASONS
// below: that array is a flat free-pick list every renderer of it treats as
// "any of these, offered with no further gate" (RideEditor's per-row menu,
// today; any future consumer that iterates the array gets the same free
// pick). Appending this value there and filtering it back out at the one
// current renderer inverts that contract — the array itself would silently
// carry a value no consumer is allowed to free-pick, a trap for the next
// reader who adds one. Any surface that offers "No Show" as a reason must
// go through the dedicated C-only action (score-api's ride.js
// markRideNoShow) and import this constant directly, never discover it by
// iterating ELIMINATION_REASONS.
export const NO_SHOW_ELIMINATION_REASON = "No Show";

// Card #2017 AC8 — a per-sheet elimination whose reason is exactly this
// string derives TERM_REASONS.SCRATCHED, not ELIMINATED, in
// deriveEliminationsTermReason below (utils/ride-status.js) — unless a
// genuine elimination cause is ALSO present, in which case the more severe
// outcome wins (see that function's own doc comment for the precedence
// rule). USEF Rulebook Chapter DR, © USEF 2026,
// docs/rules/usef/rulebook-dr-dressage-2026.pdf, verified against the
// committed PDF: "If a competitor withdraws (scratches) ... the words
// 'scratched' ... must appear after the competitor's name" — DR123.3, not a
// DR124 elimination CAUSE at all.
//
// Card #2063 (untangling this card #2052/#2053 flagged and #2017 left open,
// docs/score/term-reason-vocabulary.md §1a) — REMOVED from ELIMINATION_REASONS
// below: "Withdrawn / retired" is one of DR123.3's own outcome labels, not a
// DR124 elimination cause, so it no longer belongs in a cause-only pick
// list. The constant itself stays exported and is still load-bearing:
// deriveEliminationsTermReason (utils/ride-status.js) matches this string
// (case/punctuation-insensitively as of card #2340, alongside bare
// "withdrawal"/"withdrawn"/"withdraw" — a real prod row, entry 791, stored
// exactly the lowercase one-word form) to resolve tier 4 of
// deriveRideTermReason's 4-tier fallback for any ScoreSheet already
// carrying it as a stored elimination reason (pre-#2063 data, including
// rides among the #2056 backfill's un-migrated set) — removing the picker
// option does not and must not remove the read path for data that already
// used it. A scribe who needs to record an in-progress withdrawal now uses
// the dedicated Scratch gavel-menu action instead (#916 part A / card
// #2063 AC1, ScoringForm.jsx's "Scratch ride" section, score-api's
// setRideStatus) — this constant is kept purely for reading old sheets,
// never offered as a fresh pick again.
export const WITHDRAWAL_ELIMINATION_REASON = "Withdrawn / retired";

export const ELIMINATION_REASONS = [
  "Errors of course",
  "Off course",
  // #753 - split from the old combined "Fall of horse or rider" preset so
  // either (or both, as two separate entries) can be recorded.
  "Horse Fall",
  "Rider Fall",
  "Lameness",
  "Blood",
  "Equipment violation",
  // #2063 — "Withdrawn / retired" removed (see WITHDRAWAL_ELIMINATION_REASON's
  // own comment above for why it doesn't belong in a DR124 cause list, and
  // where a scribe now records a withdrawal instead).
];

// #1756 - how an `errors[]` entry's deduction amount was arrived at.
//
// RULE: resolved from the ride's error-of-course tier schedule
// (`resolveErrorTiers` → `errorTierForOccurrence`). Every entry scribe-fe's
// `recordError` writes is this, and it is the DEFAULT for an entry carrying
// no `amountSource` at all - which is every error stored before this card,
// so nothing already on a sheet changes meaning. One exception (#1806): an
// unmarked entry whose label is not the generated "Nth error of course" shape
// is a pre-#1756 hand-typed admin entry and is read as MANUAL
// (`isRuleResolvedError`, docs/score/admin-scoresheet-editing.md section 4.9).
//
// MANUAL: an admin typed the amount by hand in scoreadmin's RideEditor.
// That surface is a deliberate escape hatch, and the point of it is
// recording what the schedule would NOT produce, so a MANUAL entry is never
// re-priced or re-labelled by `retierRuleErrors`, and never consumes an
// error-of-course occurrence slot (an admin line reading "Late entry −2"
// must not push the next real error of course onto the 2nd tier).
//
// OTHER_DEDUCTION (#1630): one recorded occurrence of a catalog
// `otherDeductions` entry (catalog-target-schema.md section 2e): a flat,
// named, non-cumulative penalty that is NOT an error of course (eventing
// "Other Error", a USDF Overtime / Forbidden Movement, an FEI para technical
// fault). The entry carries its resolved `points`/`percent` AND an explicit
// `target` ("technical" | "artistic" | "final") copied from the catalog entry
// at record time: a section cannot be derived from an errors[] entry (#2368),
// so it is stored. Like MANUAL it is never re-priced by `retierRuleErrors`
// and never consumes an error-of-course occurrence slot, because
// `isRuleResolvedError` is true only for an explicit RULE.
export const ERROR_AMOUNT_SOURCES = {
  RULE: "rule",
  MANUAL: "manual",
  OTHER_DEDUCTION: "otherDeduction",
};

// #1630 - the vocabulary an otherDeductions entry's (and a recorded
// occurrence's) `target` is drawn from, same as the catalog schema's
// otherDeductions[].target enum.
export const OTHER_DEDUCTION_TARGETS = ["technical", "artistic", "final"];

// #1931 — how a ScoreSheet's marks got into it, recorded on
// ScoreSheet.provenance.method. Stamped ONLY by score-api's write path
// (createScoreSheet / updateScoreSheet), derived server-side from the
// caller's role and the sheet's own state — never a client-chosen field,
// so it cannot be mislabelled by the person typing (AC3).
//
// SCRIBE_LIVE:   marks entered live by the assigned scribe through
//                scribe-fe (the ordinary in-the-moment path).
// ADMIN_PAPER:   keyed by an admin from a paper test — the legitimate
//                routine paperEntry workflow (#2406/#2407), labelled.
// RECONSTRUCTED: entered after the fact by staff (an admin keying marks
//                into a sheet that was never scored live — the WEC
//                2026-08-21 case). Not an accusation; a record.
//
// A sheet with NO method (created before this card, or before #2815's
// provenance object existed) is honestly UNKNOWN: absence is never
// rendered, defaulted, or backfilled as SCRIBE_LIVE (AC4).
export const ENTRY_METHODS = {
  SCRIBE_LIVE: "scribe-live",
  ADMIN_PAPER: "admin-paper",
  RECONSTRUCTED: "reconstructed",
};

// #1934 - what is currently in a ring, stored (not derived) so the
// scoreboard/announcer never have to guess. Three statuses, not four
// (docs/score/ring-state.md): a ring that has finished a class keeps
// reporting RUNNING with that class until something replaces it - it is
// never a fourth "empty" state, on purpose (spectators are reading the
// class that just ended). BREAK/END_OF_DAY are exclusively explicit: no
// ride completing announces either one, so nothing may infer them.
//
// #2129 - CLASS_ENDED is the scribe's per-class End class press
// (docs/score/ring-truth.md rule 4): the ring's current class is cleared
// (reads as no class in progress, rule 8) but the DAY is not declared over.
// END_OF_DAY stays Score Admin's declaration. Also explicit-only, and the
// next ride or Start class returns the ring to RUNNING like BREAK does.
export const RING_STATUS = {
  RUNNING: "RUNNING",
  BREAK: "BREAK",
  END_OF_DAY: "END_OF_DAY",
  CLASS_ENDED: "CLASS_ENDED",
};

// #1934 - who last set a ring's state. SCORED is the ordinary path (a ride
// being scored writes it, costing the scribe nothing extra); EXPLICIT is an
// intentional override (Start class #1935, or Score Admin #1936/BREAK/END
// OF DAY declarations). The distinction is what lets an EXPLICIT "Start
// class" survive a stray score from the outgoing class instead of being
// clobbered by the very next write (docs/score/ring-state.md "class
// boundary" rule).
export const RING_STATE_SOURCE = {
  SCORED: "SCORED",
  EXPLICIT: "EXPLICIT",
};

// #2182 - the breed-show-scoring workstream's class-kind signal (gate card
// for #2183/#2184/#2185/#2186). FVD stamps a DSHB class with its QUALIFYING
// class's test id, so the test alone cannot tell a championship class from
// an ordinary one (see classifyDshbClassKind in
// ./utils/dshb-class-kind.js). Five kinds classify from the className/
// fvdTestId heuristic alone, matching USEF DR204/DR211: an ordinary
// (non-DSHB-special) class classifies as `null` from the heuristic, not a
// sixth heuristic value — callers that only care about these five kinds
// get an unambiguous falsy "not one of these" rather than another string
// to special-case.
export const DSHB_CLASS_KINDS = {
  CHAMPIONSHIP: "CHAMPIONSHIP",
  MATERIALE: "MATERIALE",
  SUITABILITY: "SUITABILITY",
  UNDER_SADDLE: "UNDER_SADDLE",
  // #2202 - a USDF Breeders Championship SERIES FINAL class (USEF DR211.2:
  // "At least two judges judging independently, using individual score
  // sheets, are required for USDF Breeders Championship classes"),
  // distinct from an ordinary CHAMPIONSHIP class (which stays on #2183's
  // place-only class grid, DR211.1) and from a USDFBC QUALIFYING round
  // (which is stamped with the same "champ"-pattern className but is still
  // an ordinary single-judge CHAMPIONSHIP class — DR210.5's qualifying-
  // championship clause never invokes DR211.2's two-judge floor). See
  // classifyDshbClassKind's own doc comment for the real-daysheet name
  // signal that tells a Final apart from a Qualifier.
  USDFBC_CHAMPIONSHIP: "USDFBC_CHAMPIONSHIP",
  // #2729 - the escape hatch: an explicit per-class dshbKindOverride value
  // meaning "this is not a breeding class at all, stop guessing from the
  // name". Unlike the five kinds above, classifyDshbClassKind's className/
  // fvdTestId heuristic never RETURNS this on its own - it only ever
  // arrives via dshbKindOverride (classifyDshbClassKind's `if
  // (dshbKindOverride) return dshbKindOverride` short-circuit, dshb-class-
  // kind.js), so every consumer that switches on specific kinds
  // (isDshbPlaceOnlyGridKind/isDshbScoredGridKind, the CHAMPIONSHIP/
  // USDFBC_CHAMPIONSHIP checks in isDshbHandlerClass and score-api/
  // report-api's callers) already falls through to "not DSHB" for it with
  // no code change, because none of those checks match this string. The
  // stored value MUST be exactly "NOT_DSHB": 13 real prod
  // score-class-foxvillage-main rows (classes 515/516/517/524/525/526/531/
  // 532/534/535/536/537/538) were already written with this exact string
  // as the 2026-09-17 live-show firedrill mitigation, before this enum
  // member existed to make it a supported value (card #2729).
  NOT_DSHB: "NOT_DSHB",
};

// #2730 - which of classifyDshbClassKind's two signals actually decided a
// class's kind (./utils/dshb-class-kind.js's classifyDshbClassKindWithSource).
// The card's own real case, class 534 "Great American/USDF Fourth Level FS
// A/A Champ" on test usdf-freestyle-4th-2023, is a normal Fourth Level
// Freestyle that matched CHAMPIONSHIP on the word "Champ" alone - the
// classifier's className/fvdTestId heuristic is intentionally kept (real
// daysheets carry DSHB classes with no other signal but the name), but
// nothing told a human the decision came from the name rather than a
// resolved test id, so a wrong guess routed a live ring to the #2183
// place-only grid with no way to spot or override it. NAME covers every
// name-pattern branch (CHAMPIONSHIP, USDFBC_CHAMPIONSHIP, SUITABILITY) -
// even USDFBC_CHAMPIONSHIP, which additionally checks the resolved test id
// against USDFBC_ELIGIBLE_FVD_TEST_IDS, is reached only because a name
// pattern matched first. TEST_ID covers MATERIALE/UNDER_SADDLE, which carry
// no distinguishing word at all and are resolved purely off fvdTestId. A
// `dshbKindOverride` win, or no kind resolved at all, reports `null` -
// neither signal decided it.
export const DSHB_CLASS_KIND_SIGNAL_SOURCES = {
  NAME: "NAME",
  TEST_ID: "TEST_ID",
};

// #2251 - the signals reconcile can fire for Class.testDisagreement
// (apps/score-api/src/handlers/daysheet.js). A signal never changes
// ride.testId (still the sole authoritative test edge, #435) - it is a
// read-only diagnostic recorded so the FE (#2252) can say WHY a class was
// flagged. #2257 removed the second signal, DSHB_NAME_MISMATCH: it compared
// classifyDshbClassKind for the class's test vs a ride's test with the same
// className, but that function derives Materiale / under-saddle only from
// fvdTestId, so the name cancelled out and it was a strict subset of
// CLASS_RIDE_MISMATCH. A class-name regex was rejected (hand-typed names,
// false positives on live shows). Stored rows may still carry the old value
// until their next reconcile pass; the FE falls back to a generic message.
//   CLASS_RIDE_MISMATCH - the class carries a real (non fvd-unmatched:*)
//     bound testId AND at least one of its rides resolves to a different
//     real testId. Never fires for a Test-of-Choice class: TOC's class-level
//     testId is the fvd-unmatched:9999 sentinel by design, so it never
//     qualifies as "real" here.
export const TEST_DISAGREEMENT_SIGNALS = {
  CLASS_RIDE_MISMATCH: "CLASS_RIDE_MISMATCH",
};

// #2362 (scribe-presence workstream, card A) — the scribe-fe heartbeat
// contract. Shared here (not just score-api-local) because the sender
// (#2364, scribe-fe) needs the SAME interval the write endpoint's TTL is
// sized against, per that card's AC4 ("the interval is a named constant, not
// a magic number"). Exported from score-common rather than duplicated so the
// two apps can never drift apart.
//
// PRESENCE_TTL_SECONDS is deliberately a multiple of the interval (4x), not
// an unrelated round number: it must survive a couple of missed/delayed
// beats (a slow network, a backgrounded tab) without either flapping an
// instance to "gone" between heartbeats, or leaving a truly-dead instance
// looking alive for long after it stopped. Consumers (#2363 and after) still
// own the "is this actually offline" DISPLAY threshold on top of this raw
// TTL — see the card #2362 description's "presence is an observation, not a
// judgement" rule; this constant only bounds how long a row survives with no
// further writes.
export const HEARTBEAT_INTERVAL_MS = 15000;
export const PRESENCE_TTL_SECONDS = 60;
// #2370: how long GET /presence keeps returning a row after its last
// heartbeat, tagged live:false once past PRESENCE_TTL_SECONDS, so a consumer
// can render "was here, went quiet" (and tell it from "never connected")
// across a page reload. One hour: every shipped consumer threshold (score-fe
// remote 30s, announcer-fe 60s, scoreadmin-fe 150s) is comfortably inside it,
// while a previous day's show cannot leak into a live show's response (the
// DynamoDB TTL reaper lags by days, so the window must be enforced at read).
export const PRESENCE_MAX_AGE_SECONDS = 3600;

// #3327: version of the day-key derivation stamped onto score-show's `summary`
// row (`summary.dayKeysVersion`). v2 = the TRUE venue-local day (#1823,
// fvdStampDayKey); a row with no version (or < 2) was written by the old raw
// slice(0, 10) rule, which rolls an evening class onto the next calendar day.
// Writers (score-api reconcile + summary-stream) stamp this; readers
// (scoreadmin-fe) trust summary.dayKeys ONLY at or above it and otherwise
// derive from the class list they already fetched, so a finished/idle show
// whose summary was never rewritten heals on read, with no data operation.
// Bump this whenever the day-key rule changes again.
export const SUMMARY_DAYKEYS_VERSION = 2;
